import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Descriptions,
  Tag,
  Row,
  Col,
  Divider,
  Tooltip,
  message,
  Alert
} from 'antd';
import {
  ExperimentOutlined,
  ThunderboltOutlined,
  InfoCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useMutation } from 'react-query';
import { taskManagerClient, TaskCreateRequest } from '@/utils/taskManager';
import { getVisitorId } from '@/utils/session';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// 引物设计参数接口
interface PrimerDesignParams {
  fasta_input: string;
}

// 引物信息接口
interface PrimerInfo {
  sequence: string;
  length: number;
  tm: number;
  gc_content: number;
  is_forward: boolean;
  penalty_score: number;
}

// 序列结果接口
interface SequenceResult {
  sequence_id: string;
  sequence_length: number;
  success: boolean;
  message: string;
  forward_primer?: PrimerInfo;
  reverse_primer?: PrimerInfo;
  tm_difference?: number;
  amplicon_length?: number;
}

// 嵌套的结果结构（来自格式化器）
interface NestedResult {
  total_sequences?: number;
  successful_designs?: number;
  failed_designs?: number;
  results?: SequenceResult[];
}

// 任务结果接口（统一格式）
interface TaskResult {
  total_sequences: number;
  successful_count: number;
  failed_count: number;
  results: Array<{
    sequence_id: string;
    result: SequenceResult | NestedResult;
    success: boolean;
    error?: string;
  }>;
  artifacts?: any;
  meta?: any;
}

const PrimerDesigner: React.FC = () => {
  const [form] = Form.useForm();
  const [results, setResults] = useState<TaskResult | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);

  // 安全渲染函数
  const safeRender = (value: any, fallback: string = 'N/A'): string => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string' || typeof value === 'number') return value.toString();
    return String(value);
  };

  // 安全数值渲染函数
  const safeNumber = (value: any, decimals?: number): string => {
    if (typeof value === 'number' && !isNaN(value)) {
      return decimals !== undefined ? value.toFixed(decimals) : value.toString();
    }
    return 'N/A';
  };

  // 准备FASTA输入：如果不是FASTA格式，自动添加ID
  const prepareFastaInput = (input: string): string => {
    const trimmedInput = input.trim();
    if (trimmedInput.startsWith('>')) {
      // 已经是FASTA格式
      return trimmedInput;
    } else {
      // 单序列，自动添加ID
      return `>sequence_1\n${trimmedInput}`;
    }
  };

  // 轮询任务结果
  const pollTaskResult = async (taskId: string): Promise<any> => {
    const apiBase = import.meta.env.VITE_TASK_MANAGER_URL?.replace(/\/$/, '') || '/task_manager';
    const maxAttempts = 60; // 最多轮询60次
    const pollInterval = 2000; // 每2秒轮询一次

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetch(`${apiBase}/tasks/${taskId}/status`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const taskStatus = await response.json();
        console.log(`轮询第${attempt + 1}次，任务状态:`, taskStatus.status);

        if (taskStatus.status === 'completed') {
          return taskStatus.result;
        } else if (taskStatus.status === 'failed') {
          throw new Error(taskStatus.error || '任务执行失败');
        }

        // 等待后继续轮询
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      } catch (error) {
        console.error(`轮询第${attempt + 1}次失败:`, error);
        if (attempt === maxAttempts - 1) {
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }

    throw new Error('任务超时，请检查任务状态');
  };

  // 提交任务
  const submitTaskMutation = useMutation(
    async (params: PrimerDesignParams) => {
      const visitorId = getVisitorId();
      
      const taskRequest: TaskCreateRequest = {
        name: 'PCR引物设计',
        service_name: 'clone_service',
        task_type: 'primer_design',
        handler_name: 'primer_design_predict_json',
        parameters: params
      };

      console.log('创建任务请求:', taskRequest);
      const task = await taskManagerClient.createTask(taskRequest);
      console.log('任务创建成功:', task);
      
      setCurrentTaskId(task.id);
      
      // 开始轮询结果
      const result = await pollTaskResult(task.id);
      console.log('任务完成，结果:', result);
      
      return result;
    },
    {
      onSuccess: (data) => {
        setResults(data);
        message.success('引物设计完成！');
        setCurrentTaskId(null);
      },
      onError: (error: any) => {
        console.error('引物设计失败:', error);
        message.error(`引物设计失败: ${error.message || error}`);
        setCurrentTaskId(null);
      }
    }
  );

  // 处理表单提交
  const handleSubmit = async (values: any) => {
    try {
      if (!values.sequence?.trim()) {
        message.error('请输入序列');
        return;
      }

      // 准备FASTA输入（自动为单序列添加ID）
      const fastaInput = prepareFastaInput(values.sequence);

      const params: PrimerDesignParams = {
        fasta_input: fastaInput
      };

      await submitTaskMutation.mutateAsync(params);
    } catch (error) {
      console.error('提交任务失败:', error);
    }
  };

  const copyToClipboard = (text: string, type: string) => {
    // 检查是否支持现代剪贴板API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        message.success(`${type}已复制到剪贴板`);
      }).catch(() => {
        // 如果现代API失败，使用备用方案
        fallbackCopyToClipboard(text, type);
      });
    } else {
      // 使用备用方案
      fallbackCopyToClipboard(text, type);
    }
  };

  // 备用复制方案
  const fallbackCopyToClipboard = (text: string, type: string) => {
    try {
      // 创建临时文本区域
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        message.success(`${type}已复制到剪贴板`);
      } else {
        message.error('复制失败，请手动复制');
        console.log(`${type}:`, text);
      }
    } catch (err) {
      message.error('复制失败，请手动复制');
      console.log(`${type}:`, text);
    }
  };

  // 下载所有引物序列
  const downloadAllPrimers = () => {
    if (!results || !results.results) return;
    
    // 使用与显示逻辑相同的解析方式，确保下载所有序列
    const allSequenceResults = results.results.flatMap((item, itemIndex) => {
      const nestedResult = item.result as NestedResult;
      
      // 如果有嵌套的results数组，展开它们
      if (nestedResult.results && Array.isArray(nestedResult.results)) {
        return nestedResult.results.map((seqResult, seqIndex) => ({
          ...seqResult,
          parentSuccess: item.success
        }));
      } else {
        // 单个结果
        return [{
          ...(item.result as SequenceResult),
          parentSuccess: item.success
        }];
      }
    });
    
    const content = allSequenceResults.map((seqResult, index) => {
      if (!seqResult.parentSuccess || !seqResult.success || !seqResult.forward_primer || !seqResult.reverse_primer) {
        return '';
      }
      
      const seqId = seqResult.sequence_id || `sequence_${index + 1}`;
      return [
        `>${seqId}_forward`,
        seqResult.forward_primer.sequence,
        `>${seqId}_reverse`,
        seqResult.reverse_primer.sequence
      ].join('\n');
    }).filter(Boolean).join('\n\n');
    
    if (content) {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pcr_primers.fasta';
      a.click();
      URL.revokeObjectURL(url);
      message.success('引物序列已下载');
    }
  };

  // 渲染引物卡片
  const renderPrimerCard = (primer: PrimerInfo, title: string, color: string) => (
    <Card 
      title={
        <Space>
          <ThunderboltOutlined style={{ color }} />
          {title}
        </Space>
      }
      size="small"
      extra={
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyToClipboard(primer.sequence, title)}
        >
          复制序列
        </Button>
      }
    >
      <Descriptions column={1} size="small">
        <Descriptions.Item label="序列">
          <Text code style={{ fontSize: '14px', wordBreak: 'break-all' }}>
            {primer.sequence}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="长度">
          {safeNumber(primer.length)} bp
        </Descriptions.Item>
        <Descriptions.Item label="Tm值">
          <Tag color={typeof primer.tm === 'number' && primer.tm >= 58 && primer.tm <= 65 ? 'green' : 'orange'}>
            {safeNumber(primer.tm, 1)}°C
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="GC含量">
          <Tag color={typeof primer.gc_content === 'number' && primer.gc_content >= 0.4 && primer.gc_content <= 0.6 ? 'green' : 'orange'}>
            {typeof primer.gc_content === 'number' ? safeNumber(primer.gc_content * 100, 1) : 'N/A'}%
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="惩罚得分">
          <Tag color={typeof primer.penalty_score === 'number' && primer.penalty_score < 5 ? 'green' : typeof primer.penalty_score === 'number' && primer.penalty_score < 10 ? 'orange' : 'red'}>
            {safeNumber(primer.penalty_score, 2)}
          </Tag>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      <Card>
        <Title level={3} style={{ textAlign: 'center', marginBottom: 24 }}>
          <ExperimentOutlined style={{ marginRight: 8, color: '#52c41a' }} />
          PCR引物设计
        </Title>
        
        <Paragraph style={{ textAlign: 'center', color: '#666', marginBottom: 32 }}>
          支持单条或批量FASTA序列输入，自动设计最优PCR引物对，通过任务管理器异步处理
        </Paragraph>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ sequence: '' }}
        >
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label={
                  <Space>
                    序列输入
                    <Tooltip title="支持单条序列或FASTA格式多序列输入。系统会自动清理非ATGC字符。">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="sequence"
                rules={[
                  { required: true, message: '请输入序列' },
                  { min: 20, message: '序列内容至少20个字符' }
                ]}
              >
                <TextArea
                  rows={6}
                  placeholder={
                    "支持以下格式：\n" +
                    "1. 单条序列：ATGCGTACGTAGCTAGC...\n" +
                    "2. FASTA格式：\n" +
                    ">sequence1\n" +
                    "ATGCGTACGTAGCTAG...\n" +
                    ">sequence2\n" +
                    "GCTAGCTAGCATGCGT...\n\n" +
                    "系统会自动清理非ATGC字符和格式字符。"
                  }
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ textAlign: 'center', marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitTaskMutation.isLoading}
              size="large"
              icon={<ExperimentOutlined />}
            >
              {submitTaskMutation.isLoading ? '设计中...' : '开始设计引物'}
            </Button>
          </Form.Item>
        </Form>

        {results && (
          <>
            <Divider />
            <Title level={4}>设计结果</Title>
            
            <Alert
              message={`引物设计完成`}
              description={`总共处理 ${results.total_sequences} 个序列，成功 ${results.successful_count} 个，失败 ${results.failed_count} 个`}
              type={results.successful_count > 0 ? 'success' : 'error'}
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            {/* 处理多序列结果 - 展开嵌套的results数组 */}
            {results.results.flatMap((item, itemIndex) => {
              const nestedResult = item.result as NestedResult;
              
              // 如果有嵌套的results数组，展开它们
              if (nestedResult.results && Array.isArray(nestedResult.results)) {
                return nestedResult.results.map((seqResult, seqIndex) => ({
                  ...seqResult,
                  itemIndex: itemIndex,
                  seqIndex: seqIndex,
                  parentSuccess: item.success
                }));
              } else {
                // 单个结果
                return [{
                  ...(item.result as SequenceResult),
                  itemIndex: itemIndex,
                  seqIndex: 0,
                  parentSuccess: item.success
                }];
              }
            }).map((seqResult, displayIndex) => {
              const seqId = seqResult.sequence_id || `sequence_${displayIndex + 1}`;
              const success = seqResult.parentSuccess && seqResult.success;
              
              return (
                <Card 
                  key={seqId}
                  title={`序列: ${seqId} (${seqResult.sequence_length || 0}bp)`}
                  style={{ marginBottom: 16 }}
                  size="small"
                >
                  {success ? (
                    <div>
                      <Row gutter={16}>
                        <Col span={12}>
                          {seqResult.forward_primer && renderPrimerCard(seqResult.forward_primer, '正向引物 (Forward)', '#1890ff')}
                        </Col>
                        <Col span={12}>
                          {seqResult.reverse_primer && renderPrimerCard(seqResult.reverse_primer, '反向引物 (Reverse)', '#52c41a')}
                        </Col>
                      </Row>

                      <Card title="引物对评估" style={{ marginTop: 16 }} size="small">
                        <Descriptions column={2}>
                          <Descriptions.Item label="Tm差异">
                            <Tag color={typeof seqResult.tm_difference === 'number' && seqResult.tm_difference < 3 ? 'green' : 'orange'}>
                              {safeNumber(seqResult.tm_difference, 1)}°C
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="扩增产物长度">
                            {safeNumber(seqResult.amplicon_length)} bp
                          </Descriptions.Item>
                          <Descriptions.Item label="引物对匹配度">
                            <Tag color="green">优秀</Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="建议PCR条件">
                            退火温度: {seqResult.forward_primer && seqResult.reverse_primer && 
                              typeof seqResult.forward_primer.tm === 'number' && typeof seqResult.reverse_primer.tm === 'number' ? 
                              safeNumber(Math.min(seqResult.forward_primer.tm, seqResult.reverse_primer.tm) - 5, 0) : 'N/A'}°C
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </div>
                  ) : (
                  <Alert
                    message="引物设计失败"
                    description={safeRender(seqResult.message, '未知错误')}
                    type="error"
                    showIcon
                  />
                  )}
                </Card>
              );
            })}
            
            {results.successful_count > 0 && (
              <Card title="批量结果下载" style={{ marginTop: 16 }} size="small">
                <Space>
                  <Button 
                    icon={<DownloadOutlined />} 
                    onClick={downloadAllPrimers}
                  >
                    下载所有引物序列
                  </Button>
                </Space>
              </Card>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default PrimerDesigner;